import axios from "axios";

/* =========================================
   API BASE URL
========================================= */

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://swadvedamasalasbe.onrender.com/api"
).trim();

/* =========================================
   AXIOS INSTANCE
========================================= */

const apiClient = axios.create({
  baseURL: API_URL,

  headers: {
    "Content-Type": "application/json"
  },

  timeout: 15000
});

/* =========================================
   REQUEST INTERCEPTOR
========================================= */

apiClient.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },

  (error) => {
    console.error(
      "❌ Request Error:",
      error
    );

    return Promise.reject(error);
  }
);

/* =========================================
   RESPONSE INTERCEPTOR
========================================= */

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },

  (error) => {
    // TOKEN EXPIRED / UNAUTHORIZED
    if (
      error.response?.status === 401
    ) {
      localStorage.removeItem("token");

      localStorage.removeItem("user");

      // OPTIONAL AUTO REDIRECT
      if (
        window.location.pathname !==
        "/login"
      ) {
        window.location.href =
          "/login";
      }
    }

    console.error(
      "❌ API Error:",
      error?.response?.data ||
        error.message
    );

    return Promise.reject(error);
  }
);

/* =========================================
   PRODUCT APIs
========================================= */

export const productApi = {
  getAll: () =>
    apiClient.get("/products"),

  getById: (id) =>
    apiClient.get(`/products/${id}`),

  search: (query) =>
    apiClient.get(
      `/products/search?q=${query}`
    ),

  filter: (filters) =>
    apiClient.get(
      "/products/filter",
      {
        params: filters
      }
    )
};

/* =========================================
   AUTH APIs
========================================= */

export const authApi = {
  login: (credentials) =>
    apiClient.post(
      "/auth/login",
      credentials
    ),

  signup: (userData) =>
    apiClient.post(
      "/auth/signup",
      userData
    ),

  sendEmailOtp: () =>
    apiClient.post(
      "/auth/send-email-otp"
    ),

  verifyEmailOtp: (otp) =>
    apiClient.post(
      "/auth/verify-email-otp",
      { otp }
    )
};

/* =========================================
   PROFILE APIs
========================================= */

export const profileApi = {
  get: () =>
    apiClient.get("/profile"),

  update: (userData) =>
    apiClient.put(
      "/profile",
      userData
    )
};

/* =========================================
   WALLET APIs
========================================= */

export const walletApi = {
  getBalance: () =>
    apiClient.get(
      "/wallet/balance"
    ),

  getTransactions: () =>
    apiClient.get(
      "/wallet/transactions"
    ),

  getReferralCode: () =>
    apiClient.get(
      "/wallet/referral-code"
    ),

  applyReferralCode: (
    payload
  ) =>
    apiClient.post(
      "/wallet/apply-referral",
      payload
    ),

  // ADMIN
  getAllWallets: () =>
    apiClient.get(
      "/wallet/admin/all-wallets"
    ),

  getWalletByUser: (userId) =>
    apiClient.get(
      `/wallet/admin/wallet/${userId}`
    ),

  updateWallet: (
    userId,
    payload
  ) =>
    apiClient.put(
      `/wallet/admin/update-wallet/${userId}`,
      payload
    )
};

/* =========================================
   ORDER APIs
========================================= */

export const orderApi = {
  create: (orderData) =>
    apiClient.post(
      "/orders",
      orderData
    ),

  createRazorpayOrder: (payload) =>
    apiClient.post(
      "/orders/razorpay/create",
      payload
    ),

  verifyRazorpayPayment: (payload) =>
    apiClient.post(
      "/orders/razorpay/verify",
      payload
    ),

  getById: (id) =>
    apiClient.get(
      `/orders/${id}`
    ),

  getUserOrders: () =>
    apiClient.get(
      "/orders/user/my-orders"
    )
};

/* =========================================
   CART APIs
========================================= */

export const cartApi = {
  get: () =>
    apiClient.get("/cart"),

  add: (
    productId,
    quantity
  ) =>
    apiClient.post("/cart", {
      productId,
      quantity
    }),

  update: (
    productId,
    quantity
  ) =>
    apiClient.put(
      `/cart/${productId}`,
      { quantity }
    ),

  remove: (productId) =>
    apiClient.delete(
      `/cart/${productId}`
    ),

  clear: () =>
    apiClient.delete("/cart")
};

/* =========================================
   ADMIN ORDER APIs
========================================= */

export const adminOrderApi = {
  getAll: () =>
    apiClient.get(
      "/admin/orders"
    ),

  updateStatus: (
    id,
    status
  ) =>
    apiClient.put(
      `/admin/orders/${id}`,
      { status }
    )
};

/* =========================================
   TODO / TASK MANAGEMENT APIs
========================================= */

export const todoApi = {
  // GET ALL TASKS
  getAll: () =>
    apiClient.get("/todos"),

  // GET SINGLE TASK
  getById: (id) =>
    apiClient.get(`/todos/${id}`),

  // CREATE TASK
  create: (data) =>
    apiClient.post(
      "/todos",
      data
    ),

  // UPDATE TASK STATUS
  updateStatus: (
    id,
    status
  ) =>
    apiClient.put(
      `/todos/${id}/status`,
      {
        status
      }
    ),

  // ADD COMMENT
  addComment: (
    id,
    data
  ) =>
    apiClient.put(
      `/todos/${id}/comment`,
      data
    ),

  // DELETE TASK
  deleteTask: (id) =>
    apiClient.delete(
      `/todos/${id}`
    )
};

/* =========================================
   ADMIN USER APIs
========================================= */

export const adminUserApi = {
  getAllUsers: () =>
    apiClient.get(
      "/admin/users"
    ),

  getUserById: (id) =>
    apiClient.get(
      `/admin/users/${id}`
    ),

  updateUserRole: (
    id,
    role
  ) =>
    apiClient.put(
      `/admin/users/${id}/role`,
      { role }
    ),

  deleteUser: (id) =>
    apiClient.delete(
      `/admin/users/${id}`
    )
};

/* =========================================
   FEEDBACK / COMPLAINTS APIs
========================================= */

export const feedbackApi = {
  submit: (data) =>
    apiClient.post("/feedback/submit", data),

  getAll: () =>
    apiClient.get("/feedback/all"),

  getById: (id) =>
    apiClient.get(`/feedback/${id}`),

  updateStatus: (id, status) =>
    apiClient.put(`/feedback/${id}/status`, { status })
};

/* =========================================
   EXPORT
========================================= */

export default apiClient;
